<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <post-modal
    :loading="isLoading"
    :disabled="isDisabled"
    :employee="employee"
    :title="$t('buzz.share_event')"
    @submit="onSubmit"
    @close="$emit('close', false)"
  >
    <template #header>
      <div class="orangehrm-buzz-share-event-header">
        <buzz-mention-input
          v-model="post.text"
          :rules="rules.text"
          :placeholder="$t('buzz.post_placeholder')"
        />
        <buzz-link-preview v-if="post.text" :text="post.text" />
      </div>
    </template>
    <photo-input v-model="post.photos" single />
    <div class="orangehrm-buzz-share-event-panel">
      <oxd-input-field
        v-model="post.eventName"
        :label="$t('buzz.event_name')"
        :rules="rules.eventName"
        required
      />
      <oxd-grid :cols="2" class="orangehrm-buzz-share-event-grid">
        <oxd-grid-item>
          <oxd-input-field
            v-model="post.eventDate"
            type="date"
            :label="$t('buzz.event_date')"
            :rules="rules.eventDate"
            required
          />
        </oxd-grid-item>
        <oxd-grid-item>
          <oxd-input-field
            v-model="post.eventTime"
            type="time"
            :label="$t('buzz.event_time')"
            :rules="rules.eventTime"
            required
          />
        </oxd-grid-item>
      </oxd-grid>
      <oxd-input-field
        v-model="post.location"
        :label="$t('buzz.event_location')"
        :rules="rules.location"
        required
      />
      <oxd-input-field
        v-model="post.mapLink"
        :label="$t('buzz.event_map_link')"
        :rules="rules.mapLink"
      />
    </div>
  </post-modal>
</template>

<script>
import {computed, reactive, toRefs} from 'vue';
import {formatDate} from '@/core/util/helper/datefns';
import {APIService} from '@/core/util/services/api.service';
import PostModal from '@/orangehrmBuzzPlugin/components/PostModal';
import PhotoInput from '@/orangehrmBuzzPlugin/components/PhotoInput';
import BuzzMentionInput from '@/orangehrmBuzzPlugin/components/BuzzMentionInput';
import BuzzLinkPreview from '@/orangehrmBuzzPlugin/components/BuzzLinkPreview';
import {extractFirstBuzzUrl} from '@/orangehrmBuzzPlugin/util/formatBuzzMentions';
import {
  required,
  shouldNotExceedCharLength,
} from '@/core/util/validation/rules';

export default {
  name: 'ShareEventModal',

  components: {
    'post-modal': PostModal,
    'photo-input': PhotoInput,
    'buzz-mention-input': BuzzMentionInput,
    'buzz-link-preview': BuzzLinkPreview,
  },

  props: {
    text: {
      type: String,
      default: null,
    },
    employee: {
      type: Object,
      required: true,
    },
  },

  emits: ['close'],

  setup(props, context) {
    const rules = {
      text: [shouldNotExceedCharLength(65530)],
      eventName: [required, shouldNotExceedCharLength(255)],
      eventDate: [required],
      eventTime: [required],
      location: [required, shouldNotExceedCharLength(255)],
      mapLink: [shouldNotExceedCharLength(500)],
    };
    const http = new APIService(window.appGlobal.baseUrl, '/api/v2/buzz/posts');

    const state = reactive({
      post: {
        text: props.text || null,
        eventName: null,
        eventDate: null,
        eventTime: null,
        location: null,
        mapLink: null,
        photos: [],
      },
      isLoading: false,
    });

    const onSubmit = () => {
      state.isLoading = true;
      const eventDate =
        typeof state.post.eventDate === 'string'
          ? state.post.eventDate
          : formatDate(state.post.eventDate, 'yyyy-MM-dd');

      const payload = {
        type: 'event',
        text: state.post.text,
        eventName: state.post.eventName,
        eventDate,
        eventTime: state.post.eventTime,
        location: state.post.location,
      };

      if (state.post.mapLink) {
        payload.link = state.post.mapLink;
      } else {
        const link = extractFirstBuzzUrl(state.post.text || '');
        if (link) {
          payload.link = link;
        }
      }
      if (state.post.photos.length > 0) {
        payload.photo = state.post.photos[0];
      }

      http
        .create(payload)
        .then(() => context.emit('close', true))
        .finally(() => {
          state.isLoading = false;
        });
    };

    const isDisabled = computed(
      () =>
        !state.post.eventName ||
        !state.post.eventDate ||
        !state.post.eventTime ||
        !state.post.location,
    );

    return {
      rules,
      onSubmit,
      isDisabled,
      ...toRefs(state),
    };
  },
};
</script>

<style lang="scss" scoped>
@import './event-post-form.scss';
</style>
